import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  TablePagination,
  Typography,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { ReactComponent as Edit } from "../../assets/images/edit.svg";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import Modal from "../../components/Modal";
import dayjs from "dayjs";
import styles from "./style";
import { getDateFormat } from "../../utils/dateFormatStore";

const INITIAL_FORM_STATE = {
  rebateType: "",
  minAge: "",
  maxAge: "",
  amount: "",
};

const RebatePage = ({
  rebates,
  country,
  loading,
  addLoading,
  updateLoading,
  deleteLoading,
  onAddRebate,
  onUpdateRebate,
  onDeleteRebate,
  page,
  limit,
  total,
  onChangePage,
  onChangeRowsPerPage,
  taxPeriod,
  selectedTaxYear,
  onTaxYearChange,
}) => {
  const [open, setOpen] = useState(false);
  const [formState, setFormState] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState({});
  const [viewOpen, setViewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRebate, setSelectedRebate] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const [editingRebateId, setEditingRebateId] = useState(null);
  const role = localStorage.getItem("role");
  const resetForm = () => {
    setFormState(INITIAL_FORM_STATE);
    setErrors({});
  };

  const countryPermissions = JSON.parse(
    localStorage.getItem("countryPermissions") || "{}"
  );

  const permissions = Object.values(countryPermissions)[0] || {};

  const canEditRebate = role === "SuperAdmin" ||
    permissions?.rebate?.edit === true;

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "rebateType") {
      if (value === "" || (/^[A-Za-z0-9\s]+$/.test(value) && value.length <= 40)) {
        setFormState((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
      return;
    }
    if (name === "minAge") {
      if (value === "" || /^\d{0,2}$/.test(value)) {
        setFormState((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
      return;
    }
    if (name === "maxAge") {
      if (value === "" || /^\d{0,3}$/.test(value)) {
        setFormState((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
      return;
    }
    if (name === "amount") {
      if (value === "" || /^\d+(\.\d{0,3})?$/.test(value)) {
        setFormState((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
      return;
    }
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getValidationErrors = () => {
    const validationErrors = {};
    const trimmedType = formState.rebateType.trim();
    if (!trimmedType) {
      validationErrors.rebateType = "Type is required.";
    } else if (!/^[A-Za-z0-9\s]+$/.test(trimmedType)) {
      validationErrors.rebateType = "Type must contain letters, numbers, and spaces only.";
    } else if (trimmedType.length > 40) {
      validationErrors.rebateType = "Type must be at most 40 characters.";
    }
    const parsedMinAge = Number(formState.minAge);
    if (formState.minAge === "") {
      validationErrors.minAge = "Min age is required.";
    } else if (Number.isNaN(parsedMinAge) || parsedMinAge < 0 || !Number.isInteger(parsedMinAge)) {
      validationErrors.minAge = "Min age must be a non-negative whole number.";
    } else if (formState.minAge.length > 2) {
      validationErrors.minAge = "Min age must be at most 2 digits.";
    }

    const parsedMaxAge = Number(formState.maxAge);
    if (formState.maxAge === "") {
      validationErrors.maxAge = "Max age is required.";
    } else if (Number.isNaN(parsedMaxAge) || parsedMaxAge < 0 || !Number.isInteger(parsedMaxAge)) {
      validationErrors.maxAge = "Max age must be a non-negative whole number.";
    } else if (formState.maxAge.length > 3) {
      validationErrors.maxAge = "Max age must be at most 3 digits.";
    }

    if (
      formState.minAge !== "" &&
      formState.maxAge !== "" &&
      !Number.isNaN(parsedMinAge) &&
      !Number.isNaN(parsedMaxAge) &&
      parsedMinAge > parsedMaxAge
    ) {
      validationErrors.maxAge = "Max age must be greater than or equal to min age.";
    }

    if (formState.amount === "") {
      validationErrors.amount = "Amount is required.";
    } else if (!/^\d+(\.\d{1,3})?$/.test(formState.amount)) {
      validationErrors.amount =
        "Amount must be a non-negative number with up to 3 digits after the decimal.";
    }
    return validationErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = getValidationErrors();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const payload = {
      rebateType: formState.rebateType.trim(),
    };
    payload.minAge = Number(formState.minAge);
    payload.maxAge = Number(formState.maxAge);
    payload.amount = Number(formState.amount);
    if (country) {
      payload.country = country;
    }

    const result =
      modalMode === "edit"
        ? await onUpdateRebate(editingRebateId, payload)
        : await onAddRebate(payload);
    if (result) {
      setOpen(false);
    }
  };

  const handleView = (rebate) => {
    setSelectedRebate(rebate);
    setViewOpen(true);
  };

  const handleDelete = (rebate) => {
    setSelectedRebate(rebate);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedRebate?._id) {
      setDeleteOpen(false);
      return;
    }
    const success = await onDeleteRebate(selectedRebate._id);
    if (success) {
      setDeleteOpen(false);
      setSelectedRebate(null);
    }
  };

  useEffect(() => {
    if (!open) {
      resetForm();
      setModalMode("create");
      setEditingRebateId(null);
    }
  }, [open]);

  const isSubmitDisabled = Object.keys(getValidationErrors()).length > 0;

  const handleCreateClick = () => {
    setModalMode("create");
    setEditingRebateId(null);
    resetForm();
    setOpen(true);
  };

  const handleEdit = (rebate) => {
    setModalMode("edit");
    setEditingRebateId(rebate._id || rebate.id);
    setFormState({
      rebateType: rebate.rebateType || rebate.type || "",
      minAge:
        rebate.minAge === null || rebate.minAge === undefined
          ? ""
          : String(rebate.minAge),
      maxAge:
        rebate.maxAge === null || rebate.maxAge === undefined
          ? ""
          : String(rebate.maxAge),
      amount:
        rebate.amount === null || rebate.amount === undefined
          ? ""
          : String(rebate.amount),
    });
    setErrors({});
    setOpen(true);
  };

  return (
    <>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>Rebate Management</Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Typography sx={styles.inputText}>FY</Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              value={selectedTaxYear || ""}
              onChange={onTaxYearChange}
              name="taxyear"
              displayEmpty
              sx={styles.selectFY}
              renderValue={(selected) =>
                selected
                  ? taxPeriod?.find((item) => item._id === selected)
                    ? `${dayjs(taxPeriod.find((item) => item._id === selected).start).format(getDateFormat())} - ${dayjs(taxPeriod.find((item) => item._id === selected).end).format(getDateFormat())}`
                    : "Select FY"
                  : <span style={{ color: "#aaa" }}>Select FY</span>
              }
            >
              <MenuItem value="" disabled>Select FY</MenuItem>
              {taxPeriod?.map((item) => (
                <MenuItem key={item._id} value={item._id}>
                  {dayjs(item.start).format(getDateFormat())} - {dayjs(item.end).format(getDateFormat())}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {canEditRebate && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateClick}
              sx={{ borderRadius: 40, textTransform: "capitalize" }}
            >
              + Add Rebate
            </Button>
          )}
        </Box>
      </Box>
      <Box sx={styles.container}>
        <Modal
          open={open}
          setOpen={setOpen}
          btnText={modalMode === "edit" ? "Update Rebate" : "Add Rebate"}
          handleCheck={handleSubmit}
          loading={modalMode === "edit" ? updateLoading : addLoading}
          disabled={isSubmitDisabled}
          title={modalMode === "edit" ? "Update Rebate" : "Add Rebate"}
        >
          <Box component="form">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputLabel sx={styles.label}>Type <span style={{ color: "red" }}>*</span></InputLabel>
                <TextField
                  fullWidth
                  name="rebateType"
                  value={formState.rebateType}
                  onChange={handleChange}
                  error={Boolean(errors.rebateType)}
                  helperText={errors.rebateType}
                  inputProps={{ maxLength: 40 }}
                  placeholder="Senior Citizen"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel sx={styles.label} >Min Age</InputLabel>
                <TextField
                  fullWidth
                  name="minAge"
                  type="text"
                  value={formState.minAge}
                  onChange={handleChange}
                  error={Boolean(errors.minAge)}
                  helperText={errors.minAge}
                  InputProps={{
                    inputProps: {
                      min: 0,
                      maxLength: 2,
                      inputMode: "numeric",
                      pattern: "^[0-9]{0,2}$",
                    },
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "-" || event.key === "e" || event.key === "E") {
                      event.preventDefault();
                    }
                  }}
                  placeholder="e.g. 60"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel sx={styles.label} >Max Age</InputLabel>
                <TextField
                  fullWidth
                  name="maxAge"
                  type="text"
                  value={formState.maxAge}
                  onChange={handleChange}
                  error={Boolean(errors.maxAge)}
                  helperText={errors.maxAge}
                  InputProps={{
                    inputProps: {
                      min: 0,
                      maxLength: 3,
                      inputMode: "numeric",
                      pattern: "^[0-9]{0,3}$",
                    },
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "-" || event.key === "e" || event.key === "E") {
                      event.preventDefault();
                    }
                  }}
                  placeholder="e.g. 75"
                />
              </Grid>
              <Grid item xs={12}>
                <InputLabel sx={styles.label} >Amount<span style={{ color: "red" }}>*</span></InputLabel>
                <TextField
                  fullWidth
                  name="amount"
                  type="text"
                  value={formState.amount}
                  onChange={handleChange}
                  error={Boolean(errors.amount)}
                  helperText={errors.amount}
                  InputProps={{
                    inputProps: {
                      inputMode: "decimal",
                      pattern: "^[0-9]+([.][0-9]{0,3})?$",
                    },
                  }}
                  placeholder="Enter amount"
                />
              </Grid>
            </Grid>
          </Box>
        </Modal>

        <Modal
          open={viewOpen}
          setOpen={setViewOpen}
          title="Rebate Details"
        >
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Type</Typography>
            <Typography sx={{ mb: 2 }}>{selectedRebate?.rebateType || selectedRebate?.type || "-"}</Typography>

            <Typography sx={{ fontWeight: 600, mb: 1 }}>Min Age</Typography>
            <Typography sx={{ mb: 2 }}>{selectedRebate?.minAge ?? "-"}</Typography>

            <Typography sx={{ fontWeight: 600, mb: 1 }}>Max Age</Typography>
            <Typography sx={{ mb: 2 }}>{selectedRebate?.maxAge ?? "-"}</Typography>

            <Typography sx={{ fontWeight: 600, mb: 1 }}>Amount</Typography>
            <Typography sx={{ mb: 2 }}>{selectedRebate?.amount ?? "-"}</Typography>
          </Box>
        </Modal>

        <Modal
          open={deleteOpen}
          setOpen={setDeleteOpen}
          title="Delete Rebate"
          deletetText="Delete"
          cancelText="Cancel"
          handleDelete={confirmDelete}
          loading={deleteLoading}
        >
          <Typography>Are you sure you want to delete this rebate?</Typography>
        </Modal>

        <Box sx={styles.listContainer}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Rebate List
          </Typography>
          {rebates.length === 0 && !loading ? (
            <Box sx={styles.emptyState}>
              No Data
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Min Age</TableCell>
                      <TableCell>Max Age</TableCell>
                      <TableCell>Amount</TableCell>
                      {canEditRebate && (
                        <TableCell align="center">Actions</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rebates.map((rebate) => (
                      <TableRow key={rebate._id || rebate.id}>
                        <TableCell>{rebate.rebateType || rebate.type}</TableCell>
                        <TableCell>{rebate.minAge ?? "-"}</TableCell>
                        <TableCell>{rebate.maxAge ?? "-"}</TableCell>
                        <TableCell>{rebate.amount ?? "-"}</TableCell>
                        {canEditRebate && (
                          <TableCell align="center">
                            <Button
                              size="small"
                              onClick={() => handleEdit(rebate)}
                            >
                              <Edit sx={{ color: "#878787" }} />
                            </Button>

                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleDelete(rebate)}
                            >
                              <DeleteOutlineIcon sx={{ color: "#ff0000" }} />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={Math.max(page - 1, 0)}
                onPageChange={onChangePage}
                rowsPerPage={limit}
                onRowsPerPageChange={onChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default RebatePage;
